import { Injectable, inject, signal } from '@angular/core';
import { LicensePlateDto, LicensePlateService, ListLicensePlatesRequest } from '../../api/gen';

/**
 * App-wide plate list, loaded once after login and shared by every plate autocomplete, so that
 * typing never goes to the server. Call refresh() after creating or deleting a plate.
 */
@Injectable({ providedIn: 'root' })
export class LicensePlateCacheService {
  private licensePlateService = inject(LicensePlateService);

  readonly plates = signal<LicensePlateDto[]>([]);
  readonly isLoading = signal(false);
  private loaded = false;

  /** Loads the list unless it is already loaded or loading. */
  load(): void {
    if (this.loaded || this.isLoading()) return;
    this.fetch();
  }

  refresh(): void {
    if (this.isLoading()) return;
    this.fetch();
  }

  clear(): void {
    this.plates.set([]);
    this.loaded = false;
  }

  /** Plates containing the term, compared separator-free and upper-case like stored plates. */
  search(term: string, limit = 50): LicensePlateDto[] {
    const normalized = term.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (!normalized) return [];
    const matches: LicensePlateDto[] = [];
    for (const lp of this.plates()) {
      if (lp.fullNumber?.includes(normalized)) {
        matches.push(lp);
        if (matches.length >= limit) break;
      }
    }
    return matches;
  }

  private fetch(): void {
    this.isLoading.set(true);
    // Parkers are not included: they multiply the payload and no autocomplete needs them.
    const request: ListLicensePlatesRequest = {
      pageSize: 500000,
      pageIndex: 0,
      includeParkers: false
    };
    this.licensePlateService.operatorLicensePlateFilterPost(request).subscribe({
      next: (result) => {
        this.plates.set(result.items || []);
        this.loaded = true;
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Failed to load license plates', err);
        this.isLoading.set(false);
      }
    });
  }
}
